import { Clock, FileCheck2 } from 'lucide-react'
import type { ReactNode } from 'react'

import type { QueryView } from '@krp/client-runtime'
import { LatePaymentChoice, QueryState, type LatePaymentVm } from '@krp/sale-ui'
import { Button, DiagnosticCopy } from '@krp/ui-kit'

import { formatKopecks } from '../../../shared/model/format'
import { formatMoment } from '../../../shared/model/query'
import { printingElapsedText } from '../model/fastPayment'
import { receiptWatchNote } from '../model/receiptWatch'
import { attentionLines, LATE_PAYMENT_SLIP } from '../model/selectors'
import type { AqsiSale, AqsiSaleOperation } from '../model/types'
import './background-receipts.css'

/** Список хранится на сервере: сворачивание освобождает корзину, но не скрывает долг по чеку. */
export function BackgroundReceipts({ query, children, onReload, onOpen, extra = null }: {
  query: QueryView
  children: ReactNode
  onReload: () => void
  onOpen: () => void
  /** Быстрая оплата: строки «Чек пробит» продаж, которых в списке отложенных уже нет. */
  extra?: ReactNode
}) {
  if (query.status === 'empty' && extra === null) return null
  return <section className="aqsi-background-receipts" aria-label="Чеки в фоне">
    <header><strong>Чеки в фоне</strong><Button variant="ghost" onClick={onOpen}>Открыть список</Button></header>
    {query.status !== 'empty' && <QueryState requestIdHidden query={query} what="Чеки в фоне" emptyText="Ожидающих чеков нет" onRetry={onReload} />}
    <div className="aqsi-background-receipts__list">{extra}{children}</div>
    <small>Можно собирать следующую корзину. Во время сверки списания новая оплата недоступна. Завершённые чеки уходят в журнал.</small>
  </section>
}

/** Только факты сервера; отсутствие ответа не превращается в успешную фискализацию. */
export function BackgroundReceiptCard({ sale, operation, query, now, onReload, onResolve, onLatePayment, latePaymentBusy = false, onReturn }: {
  sale: AqsiSale
  operation: AqsiSaleOperation | undefined
  query: QueryView | null
  now: number
  onReload: () => void
  onResolve?: (() => void) | undefined
  /** Решение по запоздавшему слипу (`late-payment-slip`): `true` - «Оплата прошла — пробить чек»; нет - кнопок нет. */
  onLatePayment?: ((paid: boolean) => void) | undefined
  /** Команда по слипу в пути: кнопки заблокированы. */
  latePaymentBusy?: boolean | undefined
  /** Чек с проблемой: вернуть продажу на кассу, чтобы решить её в окне оплаты («Повторить проведение», «Отложить»…). Нет — кнопки нет. */
  onReturn?: (() => void) | undefined
}) {
  const late = sale.attention.find((item) => item.code === LATE_PAYMENT_SLIP)
  const latePayment: LatePaymentVm | null = late === undefined || onLatePayment === undefined ? null
    : { text: attentionLines([late]).join(' '), canAct: !latePaymentBusy, sending: latePaymentBusy, blockedText: null }
  if (['InProgress', 'Unknown', 'Failed'].includes(sale.payment)) {
    const purchase = operation?.saleId === sale.id && operation.operationId === sale.purchaseOperationId && operation.kind === 'purchase' ? operation : undefined
    const text = sale.payment === 'Failed' ? (latePayment !== null ? 'Оплата отменена, но после отмены найден слип покупки' : 'Оплата отменена. Деньги не списаны.') : purchase?.nextAction === 'ManualReconciliation'
      ? 'Исход оплаты не подтверждён — требуется ручная сверка' : 'Проверяем списание после отмены оплаты'
    const message = purchase?.providerMessage ?? sale.lastMessage
    return <article className="aqsi-background-receipts__card" data-tone={sale.payment === 'Unknown' ? 'warning' : undefined}>
      <div><Clock size={16} aria-hidden="true" /> <strong>{formatKopecks(sale.totalKopecks)}</strong><small>{formatMoment(sale.createdAt)}</small></div>
      <p>{text}</p>
      <small>{sale.lines.map((line) => line.name).join(', ')}</small>
      {message !== null && <><p role="status">{message}</p><DiagnosticCopy text={[text, message].join('\n')} /></>}
      {latePayment !== null && onLatePayment !== undefined && <LatePaymentChoice latePayment={latePayment} onResolve={onLatePayment} />}
      {purchase?.nextAction === 'ManualReconciliation' && onResolve !== undefined && <Button onClick={onResolve}>Ручная сверка</Button>}
      {query !== null && <QueryState query={query} what="Статус оплаты" emptyText="Статус пока не получен" onRetry={onReload} />}
    </article>
  }
  const current = operation?.saleId === sale.id && operation.operationId === sale.receiptOperationId && operation.kind === 'receipt' ? operation : undefined
  const failed = sale.fiscal === 'Failed' || current?.state === 'Failed'
  const done = sale.fiscal === 'Succeeded' || sale.fiscal === 'IssuedOutside' || (current?.state === 'Succeeded' && current.resultId !== null)
  const status = done ? 'Чек пробит' : failed || sale.receiptOperationId === null ? 'Чек не пробит — требуется внимание' : current?.providerStatus === 'Pending' ? 'Ожидает кассу' :
    current?.providerStatus === 'Processing' || current?.providerStatus === 'Finishing' ? 'Касса пробивает чек' :
      'Ожидает подтверждения чека'
  const note = receiptWatchNote(current, now)
  const problem = failed || sale.attention.length > 0
  const elapsed = done || failed ? null : printingElapsedText(current?.sentAt ?? sale.updatedAt, now)
  return <article className="aqsi-background-receipts__card" data-tone={failed || sale.attention.length > 0 ? 'warning' : undefined}>
    <div>{done ? <FileCheck2 size={16} aria-hidden="true" /> : <Clock size={16} aria-hidden="true" />} <strong>{formatKopecks(sale.totalKopecks)}</strong><small>{formatMoment(sale.createdAt)}</small></div>
    <p>Оплачено · {status}</p>
    {elapsed !== null && <small role="status">Печатаем чек · идёт {elapsed}</small>}
    <small>{sale.lines.map((line) => line.name).join(', ')}</small>
    {note !== null && <small>{note}</small>}
    {sale.lastMessage !== null && (failed || current === undefined) && <p role="status">{sale.lastMessage}</p>}
    {sale.attention.map((attention) => <p key={attention.code} role="alert">{attention.text}</p>)}
    {problem && onReturn !== undefined && <>
      <p role="alert">С чеком проблема: новая оплата недоступна, пока вы её не решите.</p>
      <Button onClick={onReturn}>Вернуть на кассу и решить</Button>
    </>}
    {query !== null && <QueryState query={query} what="Статус чека" emptyText="Статус пока не получен" onRetry={onReload} />}
    {query?.refreshing === true && <small role="status">Обновляем статус…</small>}
  </article>
}

/** Быстрая оплата: чек фоновой продажи пробит. Строка держится несколько секунд или до «Понятно»; сама продажа в списке отложенных уже не числится. */
export function BackgroundReceiptDone({ totalText, receiptId, onDismiss }: { totalText: string; receiptId: string | null; onDismiss: () => void }) {
  return <article className="aqsi-background-receipts__card" data-tone="success" role="status">
    <div><FileCheck2 size={16} aria-hidden="true" /> <strong>Чек пробит{receiptId !== null ? ` № ${receiptId}` : ''}</strong>{totalText !== '' && <small>{totalText}</small>}</div>
    <Button variant="ghost" onClick={onDismiss}>Понятно</Button>
  </article>
}
